// Shared UPRM lock: first-time users (inside or outside UK) stay off JourneyOS
// until their first assessment finishes. Returning users who already have an
// assessment (or an unlock flag) get a normal UPRM welcome on sign-in.
import { supabase } from '@/lib/supabase';

export const INSIDE_UK_WIZARD_ROUTE = '/journey-launch-inside';
export const OUTSIDE_UK_WIZARD_ROUTE = '/journey-entrance';

export interface UprmLockStatus {
  locked: boolean;
  residency: 'already-in-uk' | 'outside-uk' | null;
}

export function wizardRouteFor(residency: UprmLockStatus['residency']): string {
  return residency === 'already-in-uk' ? INSIDE_UK_WIZARD_ROUTE : OUTSIDE_UK_WIZARD_ROUTE;
}

function resolveResidency(steps: Record<string, unknown>): UprmLockStatus['residency'] {
  const residencyRaw = steps.residencyStatus || steps.assessmentPath;
  if (residencyRaw === 'already-in-uk' || residencyRaw === 'outside-uk') return residencyRaw;
  return null;
}

export async function getUprmLockStatus(userId: string): Promise<UprmLockStatus> {
  try {
    const [{ data: mem }, { data: state }, assessCountRes] = await Promise.all([
      supabase
        .from('onboarding_memory')
        .select('steps, completed')
        .eq('user_id', userId)
        .maybeSingle(),
      supabase
        .from('journey_states')
        .select('current_stage, wizard_handover')
        .eq('user_id', userId)
        .maybeSingle(),
      supabase
        .from('compass_simulations')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', userId)
        .eq('simulation_type', 'assessment'),
    ]);

    const steps = (mem?.steps || {}) as Record<string, unknown>;
    const residency = resolveResidency(steps);

    // Explicit unlock (AssessmentResults / Inside UK saveHandover).
    if (steps.uprmUnlocked === true) {
      return { locked: false, residency };
    }
    if (state?.wizard_handover || state?.current_stage === 'wizard_complete') {
      return { locked: false, residency };
    }

    // Returning users who already ran an assessment → open UPRM and seed welcome data.
    const assessCount = assessCountRes.count ?? 0;
    if (assessCount > 0) {
      void activateUprmAfterFirstAssessment(userId);
      return { locked: false, residency };
    }

    // Incomplete onboarding is handled by AppLayout → /onboarding.
    if (!mem?.completed) {
      return { locked: false, residency };
    }

    // First-time only: completed onboarding, no assessment yet → locked.
    if (residency === 'already-in-uk' || residency === 'outside-uk') {
      return { locked: true, residency };
    }

    // Legacy accounts with no residency marker stay open.
    return { locked: false, residency };
  } catch (err) {
    console.error('getUprmLockStatus error:', err);
    return { locked: false, residency: null };
  }
}

export async function markUprmUnlocked(userId: string): Promise<void> {
  try {
    const { data: row } = await supabase
      .from('onboarding_memory')
      .select('steps')
      .eq('user_id', userId)
      .maybeSingle();
    const steps = { ...((row?.steps as Record<string, unknown>) || {}), uprmUnlocked: true };
    await supabase.from('onboarding_memory').upsert(
      {
        user_id: userId,
        steps,
        last_updated_at: new Date().toISOString(),
      },
      { onConflict: 'user_id' },
    );
  } catch (err) {
    console.error('markUprmUnlocked error:', err);
  }
}

/** Unlock UPRM and seed/advance journey_states so the dashboard can welcome the user. */
export async function activateUprmAfterFirstAssessment(
  userId: string,
  opts?: { destination?: string; pathway?: string },
): Promise<void> {
  await markUprmUnlocked(userId);

  try {
    const { data: existing } = await supabase
      .from('journey_states')
      .select('milestones, next_actions, current_stage')
      .eq('user_id', userId)
      .maybeSingle();

    const now = new Date().toISOString();
    const priorMilestones = Array.isArray(existing?.milestones) ? existing!.milestones : [];
    const milestones =
      priorMilestones.length > 0
        ? priorMilestones.map((m: { id?: string; status?: string; completed_at?: string }) => {
            if (
              m?.id === 'assessment_start' ||
              m?.id === 'first_wizard' ||
              m?.id === 'assessment'
            ) {
              return { ...m, status: 'completed', completed_at: m.completed_at || now };
            }
            return m;
          })
        : [
            {
              id: 'onboarding_complete',
              name: 'Onboarding Complete',
              status: 'completed',
              completed_at: now,
            },
            {
              id: 'assessment_start',
              name: 'Visa Assessment',
              status: 'completed',
              completed_at: now,
            },
            { id: 'document_collection', name: 'Document Collection', status: 'pending' },
            { id: 'application_submission', name: 'Application Submission', status: 'pending' },
          ];

    const next_actions = [
      { id: 'collect_documents', label: 'Collect required documents', priority: 'high' },
      { id: 'review_pathway', label: 'Review your pathway plan', priority: 'medium' },
      ...(opts?.pathway
        ? [{ id: 'continue_pathway', label: `Continue ${opts.pathway}`, priority: 'high' }]
        : []),
    ];

    await supabase.from('journey_states').upsert(
      {
        user_id: userId,
        current_stage: 'documents',
        milestones,
        next_actions,
        updated_at: now,
      },
      { onConflict: 'user_id' },
    );
  } catch (err) {
    console.error('activateUprmAfterFirstAssessment error:', err);
  }
}
